import type { THistoryFrame, THistoryMessageItem } from "@/models/chat.model";

import { DEFAULT_OPERATOR_LABEL } from "@/constants/common";

import { formatDate } from '@/utils';
import { IMessage, TMessageSender } from "./context";
import { ISavedMessage } from "./types";

export const mapFromUserHistory = (messages?: ISavedMessage[]): IMessage[] =>
  (messages ?? []).map((message) => ({
    sender: message?.sender || 'USER',
    message: message?.message,
    date: message?.date,
    agentName: message?.agentName,
    cards: message?.cards,
    comparisonData: message?.comparisonData,
    suggestedActions: message?.suggestedActions,
    seq: message?.seq,
    label: message?.label,
    tone: message?.tone,
    type: "html",
  }));

export const mapToUserHistory = (messages?: IMessage[]): ISavedMessage[] =>
  (messages ?? []).map((message) => ({
    sender: message?.sender || "USER",
    message: message?.message?.toString() || "",
    date: formatDate(message?.date),
    agentName: message?.agentName,
    cards: message?.cards,
    comparisonData: message?.comparisonData,
    suggestedActions: message?.suggestedActions,
    seq: message?.seq,
    label: message?.label,
    tone: message?.tone,
  }));

const HISTORY_ROLE_TO_SENDER: Record<THistoryMessageItem["role"], TMessageSender> = {
  user: "USER",
  assistant: "AI",
  operator: "OPERATOR",
};

const messageKey = (sender: string, text: string): string => `${sender}|${text}`;

const toTimestampMs = (date?: Date | string): number => (date ? new Date(date).getTime() || 0 : 0);
const compareByDate = (a: IMessage, b: IMessage): number => toTimestampMs(a.date) - toTimestampMs(b.date);

export const mergeHistory = (frame: THistoryFrame, existing: IMessage[]): IMessage[] => {
  const existingKeys = new Set<string>();
  existing.forEach((message) => {
    if (!message.sender) return;
    existingKeys.add(messageKey(message.sender, message.message?.toString() ?? ""));
  });

  const missing = frame.messages.reduce<IMessage[]>((acc, item) => {
    const sender = HISTORY_ROLE_TO_SENDER[item.role];
    if (!sender) return acc;

    const key = messageKey(sender, item.text);
    if (existingKeys.has(key)) return acc;
    existingKeys.add(key);

    acc.push({
      message: item.text,
      sender,
      label: sender === "OPERATOR" ? DEFAULT_OPERATOR_LABEL : undefined,
      date: item.created_at,
    });
    return acc;
  }, []);

  return missing.length > 0 ? [...existing, ...missing].sort(compareByDate) : existing;
};
